import { createElement } from 'react'

// Dispatched on `window` to ask the form wizard to pop a step. Listened for by
// HealthForm — the header that triggers it lives in the layout, outside the
// form's component tree.
export const HEALTH_FORM_BACK_EVENT = 'health-form-back'

export const FAMILY_TYPE_SINGLE = 0;
export const FAMILY_TYPE_COUPLE = 1;
export const FAMILY_TYPE_SINGLE_WITH_CHILDREN = 2;
export const FAMILY_TYPE_COUPLE_WITH_CHILDREN = 3;

export const SOCIAL_REGIME_GENERAL = 0;
export const SOCIAL_REGIME_ALSACE_MOSELLE = 1;
export const SOCIAL_REGIME_TNS = 2;
export const SOCIAL_REGIME_EXPATRIATE = 3;
export const SOCIAL_REGIME_FONCTIONNAIRE = 4;
export const SOCIAL_REGIME_AGRICOLE = 5;

export const SOCIAL_REGIME = {
    [SOCIAL_REGIME_GENERAL]: 'Général',
    [SOCIAL_REGIME_ALSACE_MOSELLE]: 'Alsace - Moselle',
    [SOCIAL_REGIME_TNS]: 'TNS (Travailleur Non Salarié)',
    [SOCIAL_REGIME_EXPATRIATE]: 'Expatrié',
    [SOCIAL_REGIME_FONCTIONNAIRE]: 'Fonctionnaire',
    [SOCIAL_REGIME_AGRICOLE]: 'Agricole',
} as const;

export const SOCIAL_REGIME_CHOICES = {
    'general': SOCIAL_REGIME_GENERAL,
    'alsace-moselle': SOCIAL_REGIME_ALSACE_MOSELLE,
    'tns': SOCIAL_REGIME_TNS,
    'expatriate': SOCIAL_REGIME_EXPATRIATE,
    'fonctionnaire': SOCIAL_REGIME_FONCTIONNAIRE,
    'agricole': SOCIAL_REGIME_AGRICOLE,
} as const;

export const JOB_FARMER = 0;
export const JOB_CRAFTMAN = 1;
export const JOB_STOREKEEPER = 2;
export const JOB_CEO = 3;
export const JOB_EXECUTIVE = 4;
export const JOB_CIVIL_SERVANT = 5;
export const JOB_EMPLOYEE = 6;
export const JOB_WORKER = 7;
export const JOB_STUDENT = 8;
export const JOB_NONE = 9;
export const JOB_FREELANCE = 10;
export const JOB_RETIRED = 11;
export const JOB_INSURANCE_AGENT = 12;
export const COLLABORATING_PARTNERS = 13;
export const JOB_JOBSEEKER = 14;
export const JOB_COMPANY_MANAGER = 15;
export const JOB_MEDICAL_EMPLOYEE = 16;
export const JOB_MEDICAL_NON_EMPLOYEE = 17;

export const JOBS = {
    [JOB_FARMER]: 'Agriculteurs exploitants',
    [JOB_CRAFTMAN]: 'Artisans',
    [JOB_STOREKEEPER]: 'Commerçant et assimilés',
    [JOB_CEO]: 'Chefs d\'entreprise',
    [JOB_EXECUTIVE]: 'Cadres',
    [JOB_CIVIL_SERVANT]: 'Employés de la fonction publique',
    [JOB_EMPLOYEE]: 'Employés, agents de maîtrise',
    [JOB_WORKER]: 'Ouvriers',
    [JOB_STUDENT]: 'Étudiant',
    [JOB_NONE]: 'Personne sans activité, demandeur d\'emplois',
    [JOB_FREELANCE]: 'Profession libérales',
    [JOB_RETIRED]: 'Retraités',
    [JOB_INSURANCE_AGENT]: 'Agent d\'assurance',
    [COLLABORATING_PARTNERS]: 'Conjoint collaborateur',
    [JOB_JOBSEEKER]: 'Demandeur d\'emploi',
    [JOB_COMPANY_MANAGER]: 'Gérant de société',
    [JOB_MEDICAL_EMPLOYEE]: 'Professions médicales et para médicales Salariés',
    [JOB_MEDICAL_NON_EMPLOYEE]: 'Professions médicales et para médicales Non Salariés',
} as const;

export const JOBS_CHOICES = [
    {
        job: 'Salarié',
        regime_id: SOCIAL_REGIME_GENERAL,
        job_id: JOB_EMPLOYEE,
    },
    {
        job: 'Retraité',
        regime_id: SOCIAL_REGIME_GENERAL,
        job_id: JOB_RETIRED,
    },
    {
        job: 'Demandeur d\'emploi',
        regime_id: SOCIAL_REGIME_GENERAL,
        job_id: JOB_JOBSEEKER,
    },
    {
        job: 'Freelance (TNS, indépendant)',
        short_name: 'Freelance',
        regime_id: SOCIAL_REGIME_TNS,
        job_id: JOB_FREELANCE,
    },
    {
        job: 'Fonctionnaire',
        regime_id: SOCIAL_REGIME_FONCTIONNAIRE,
        job_id: JOB_CIVIL_SERVANT,
    },
    {
        job: 'Sans profession',
        regime_id: SOCIAL_REGIME_GENERAL,
        job_id: JOB_NONE,
    },
    {
        job: 'Chef d\'entreprise',
        regime_id: SOCIAL_REGIME_TNS,
        job_id: JOB_CEO,
    },
    {
        job: 'Étudiant',
        regime_id: SOCIAL_REGIME_GENERAL,
        job_id: JOB_STUDENT,
    },
    {
        job: 'Chef d\'exploitation agricole',
        short_name: 'Agriculteur',
        regime_id: SOCIAL_REGIME_AGRICOLE,
        job_id: JOB_FARMER,
    },
    {
        job: 'Expatrié',
        regime_id: SOCIAL_REGIME_EXPATRIATE,
        job_id: 99, // Faked it's not a real job
    },
    {
        job: 'Travailleur frontalier suisse',
        short_name: 'Travailleur suisse',
        regime_id: SOCIAL_REGIME_GENERAL,
        job_id: 666,
    },
];

const faIcon = (name: string) => createElement('i', { className: `fa-regular ${name}`, 'aria-hidden': true })

const JOB_ICONS: Record<number, string> = {
    [JOB_EMPLOYEE]: 'fa-briefcase',
    [JOB_RETIRED]: 'fa-couch',
    [JOB_JOBSEEKER]: 'fa-magnifying-glass',
    [JOB_FREELANCE]: 'fa-laptop',
    [JOB_CIVIL_SERVANT]: 'fa-building-columns',
    [JOB_NONE]: 'fa-ban',
    [JOB_CEO]: 'fa-building',
    [JOB_STUDENT]: 'fa-graduation-cap',
    [JOB_FARMER]: 'fa-seedling',
    99: 'fa-globe',
    666: 'fa-arrow-right-arrow-left',
};

export const JOB_SELECT_OPTIONS = JOBS_CHOICES.map((job) => ({
    label: job.job,
    value: job.job_id,
    icon: faIcon(JOB_ICONS[job.job_id]),
}));

export const FAMILY_TYPE_OPTIONS = [
    { label: 'Un adulte', value: FAMILY_TYPE_SINGLE, icon: faIcon('fa-user') },
    { label: 'Un couple', value: FAMILY_TYPE_COUPLE, icon: faIcon('fa-user-group') },
    { label: 'Un adulte + enfant(s)', value: FAMILY_TYPE_SINGLE_WITH_CHILDREN, icon: faIcon('fa-family-pants') },
    { label: 'Un couple + enfant(s)', value: FAMILY_TYPE_COUPLE_WITH_CHILDREN, icon: faIcon('fa-users') },
];

export const CHILDREN_COUNT_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
    label: `${n} enfant${n > 1 ? 's' : ''}`,
    value: n,
    icon: faIcon('fa-user'),
}));

export const TYPE_MAIN = 'main';
export const TYPE_PARTNER = 'partner';
export const TYPE_CHILD = 'children';

export const AGRICULTURAL_STATUS_OWNER = 0;
export const AGRICULTURAL_STATUS_SALARIED = 1;

export const AGRICULTURAL_STATUS = {
    [AGRICULTURAL_STATUS_OWNER]: 'Exploitant',
    [AGRICULTURAL_STATUS_SALARIED]: 'Salarié',
} as const;

export const AGRICULTURAL_STATUS_OPTIONS = [
    { label: AGRICULTURAL_STATUS[AGRICULTURAL_STATUS_OWNER], value: AGRICULTURAL_STATUS_OWNER, icon: faIcon('fa-tractor') },
    { label: AGRICULTURAL_STATUS[AGRICULTURAL_STATUS_SALARIED], value: AGRICULTURAL_STATUS_SALARIED, icon: faIcon('fa-briefcase') },
];

export const ALSACE_MOSELLE_HINT =
    "Les assurés résidant dans les départements d'Alsace-Moselle ne payent pas les mêmes montants de cotisations et sont mieux remboursés que les autres assurés du reste de la France.";

export const GENDER_FEMALE = 1;
export const GENDER_MALE = 0;

export const CIVILITY = {
    [GENDER_FEMALE]: 'Madame',
    [GENDER_MALE]: 'Monsieur',
} as const;

export const MIN_AGE = 18;
export const MAX_AGE = 110;
// Children of insured adults are considered dependents up to age 26
// (matches the legacy Vue validator at HealthForm.vue:1777).
export const MAX_AGE_CHILD = 26;

// Cookie that remembers the prospect's last health-comparator quote token.
// Set on submit (and on reuse_token re-post) so returning visits can pick up
// the existing quote instead of creating a fresh one. Mirrors the cookie
// HealthForm.vue uses (`health_quote_stored_token`, 30-day TTL).
export const STORED_TOKEN_COOKIE = 'health_quote_stored_token';
export const STORED_TOKEN_TTL_DAYS = 30;

// Sentinel "job" IDs used by HealthForm.vue (line 2902): selecting either as
// the main contractor's profession exits the standard wizard and lands on a
// partner-specific URL instead of advancing to the next step. Swiss frontalier
// also fires a backend lead-transmission before redirecting.
export const JOB_EXPATRIATE = 99;
export const JOB_SWISS_BORDER_WORKER = 666;
export const EXPATRIATE_REDIRECT_URL = 'https://reassurez-moi.fr/guide/mutuelle-sante/expatries';
export const SWISS_BORDER_WORKER_REDIRECT_URL = 'https://guide.reassurez-moi.fr/guide/partenaire/frontalier-suisse/lafrontaliere/lafrontaliere';
